// EditBannerGroup.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../../../axiosConfig';

/**
 * Edit Page for Banner Group
 *
 * 1) GET view:   /internal-api/v1/client-web/cms/page/page-blocks/{id}/view
 * 2) POST edit:  /internal-api/v1/client-web/cms/page/page-blocks/banner-group/{id}/edit-group
 *    - only extra param compared to create: top-level "id"
 *
 * Notes:
 * - API requires datetime format "Y-m-d H:i" or "Y-m-d H:i:s" -> we send "Y-m-d H:i"
 * - datetime-local input uses "YYYY-MM-DDTHH:mm" -> we convert both ways
 * - Images: if user DOES NOT select a new file, we DO NOT append file fields so backend can keep existing
 */

const EditBannerGroup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('blockId');
  const pageMeta = searchParams.get('pageMeta') || '';
  const pageId = searchParams.get('pageId') || '';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [payload, setPayload] = useState(null);

  // ---------- Date helpers ----------
  const toApiDateTime = (value, withSeconds = false) => {
    // input from datetime-local: "YYYY-MM-DDTHH:mm"
    if (!value) return '';
    const base = value.replace('T', ' '); // "YYYY-MM-DD HH:mm"
    return withSeconds ? `${base}:00` : base;
  };

  const toDateTimeLocal = (apiValue) => {
    // input from API: "YYYY-MM-DD HH:mm:ss" OR "YYYY-MM-DD HH:mm"
    if (!apiValue) return '';
    // take first 16 chars "YYYY-MM-DD HH:mm", replace space -> "T"
    const base = apiValue.trim().slice(0, 16);
    return base.replace(' ', 'T');
  };

  // ---------- Factories ----------
  const createEmptyBannerContent = () => ({
    alt: '',
    isClickable: 0,
    clickLink: '',
    displayOrder: 1,
    validFrom: '',
    validTill: '',
    imageFile: null,
    mobileImageFile: null,
    // view-only fields (for preview)
    webImgPath: '',
    mobImgPath: ''
  });

  const createEmptyBanner = () => ({
    mainTitle: '',
    subTitle: '',
    shortDescription: '',
    description: '',
    groupOrder: 1,
    validFrom: '',
    validTill: '',
    isActionButtonEnable: 0,
    actionButtonModal: {
      text: '',
      hrefLink: '',
      isNewTab: 0,
      validFrom: '',
      validTill: ''
    },
    bannerContents: [createEmptyBannerContent()]
  });

  const createEmptyBannerGroup = () => ({
    banners: [createEmptyBanner()] // one banner per group
  });

  // ---------- Build initial payload from GET response ----------
  const mapViewToEditPayload = (result) => {
    const d = result?.data || {};

    // carousels[] -> each carousel has bannerGroups[] (your banners)
    // We want: payload.bannerGroups[] where each has banners[0]
    const bannerGroups = [];

    const carousels = Array.isArray(d.carousels) ? d.carousels : [];
    carousels.forEach((carousel) => {
      const bgs = Array.isArray(carousel.bannerGroups) ? carousel.bannerGroups : [];
      bgs.forEach((bg) => {
        // contents[] from API
        const contents = Array.isArray(bg.contents) ? bg.contents : [];

        const mappedContents =
          contents.length > 0
            ? contents.map((ct) => ({
                alt: ct.alt || '',
                isClickable: ct.isClickable ? 1 : 0,
                clickLink: ct.clickLink || '',
                displayOrder: ct.displayOrder ?? 1,
                validFrom: toDateTimeLocal(ct.validFrom),
                validTill: toDateTimeLocal(ct.validTill),
                imageFile: null,
                mobileImageFile: null,
                webImgPath: ct.webImgPath || '',
                mobImgPath: ct.mobImgPath || ''
              }))
            : [createEmptyBannerContent()];

        // action button mapping (API might provide actionButton null)
        // We keep modal fields editable only if enabled
        const isActionEnabled = !!bg.isActionButtonEnabled;

        const mappedBanner = {
          mainTitle: bg.mainTitle || '',
          subTitle: bg.subTitle || '',
          shortDescription: bg.shortDescription || '',
          description: bg.description || '',
          groupOrder: bg.groupOrder ?? 1,
          validFrom: '', // API doesn't provide banner-level validFrom/validTill in sample
          validTill: '',
          isActionButtonEnable: isActionEnabled ? 1 : 0,
          actionButtonModal: {
            text: bg.actionButton?.text || '',
            hrefLink: bg.actionButton?.hrefLink || '',
            isNewTab: bg.actionButton?.isNewTab ? 1 : 0,
            validFrom: toDateTimeLocal(bg.actionButton?.validFrom),
            validTill: toDateTimeLocal(bg.actionButton?.validTill)
          },
          bannerContents: mappedContents
        };

        bannerGroups.push({ banners: [mappedBanner] });
      });
    });

    return {
      // additional required param for edit:
      id: result?.id,

      // top-level fields for block
      pageCode: '', // not provided in view result; keep empty or set if you know it
      name: d.content || '', // in your view, "content" seems like block content/name
      displayOrder: result?.displayOrder ?? 1,

      blockHeader: d.title ?? '-',
      subHeader: d.subTitle ?? '-',
      blockContent: d.content ?? '',
      link: d.viewMoreLink ?? '',
      validFrom: '', // not provided at block level in sample; keep editable blank
      validTill: '',
      isPublic: d.isPublic ? 1 : 0,
      isVisible: d.isVisible ? 1 : 0,

      bannerGroups: bannerGroups.length ? bannerGroups : [createEmptyBannerGroup()]
    };
  };

  // ---------- GET load ----------
  const loadForEdit = async () => {
    try {
      setLoading(true);
      setErrorMsg('');

      const res = await axiosInstance.get(
        `/internal-api/v1/client-web/cms/page/page-blocks/${id}/view`
      );

      if (!res?.data?.status) {
        setErrorMsg(res?.data?.error?.message || 'Failed to load data');
        setPayload(null);
        return;
      }

      const editPayload = mapViewToEditPayload(res.data.result);
      setPayload(editPayload);
    } catch (err) {
      console.error('EditBannerGroup load error:', err);
      setErrorMsg(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Request failed'
      );
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) loadForEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ---------- Setters ----------
  const setTopField = (name, value) => setPayload((p) => ({ ...p, [name]: value }));
  const setTopNumberField = (name, value) =>
    setPayload((p) => ({ ...p, [name]: value === '' ? '' : Number(value) }));

  const setBannerField = (groupIndex, bannerIndex, field, value) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      const group = { ...groups[groupIndex] };
      const banners = [...group.banners];
      const banner = { ...banners[bannerIndex], [field]: value };

      if (field === 'isActionButtonEnable' && Number(value) === 0) {
        banner.actionButtonModal = {
          text: '',
          hrefLink: '',
          isNewTab: 0,
          validFrom: '',
          validTill: ''
        };
      }

      banners[bannerIndex] = banner;
      group.banners = banners;
      groups[groupIndex] = group;

      return { ...p, bannerGroups: groups };
    });
  };

  const setActionButtonField = (groupIndex, bannerIndex, field, value) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      const group = { ...groups[groupIndex] };
      const banners = [...group.banners];
      const banner = { ...banners[bannerIndex] };

      banner.actionButtonModal = { ...banner.actionButtonModal, [field]: value };

      banners[bannerIndex] = banner;
      group.banners = banners;
      groups[groupIndex] = group;

      return { ...p, bannerGroups: groups };
    });
  };

  const setContentField = (groupIndex, bannerIndex, contentIndex, field, value) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      const group = { ...groups[groupIndex] };
      const banners = [...group.banners];
      const banner = { ...banners[bannerIndex] };
      const contents = [...banner.bannerContents];

      contents[contentIndex] = { ...contents[contentIndex], [field]: value };
      banner.bannerContents = contents;

      banners[bannerIndex] = banner;
      group.banners = banners;
      groups[groupIndex] = group;

      return { ...p, bannerGroups: groups };
    });
  };

  // ---------- Add/remove group (banner) ----------
  const addBannerGroup = () => {
    setPayload((p) => ({
      ...p,
      bannerGroups: [...p.bannerGroups, createEmptyBannerGroup()]
    }));
  };

  const removeBannerGroup = (groupIndex) => {
    setPayload((p) => {
      const next = p.bannerGroups.filter((_, i) => i !== groupIndex);
      return { ...p, bannerGroups: next.length ? next : p.bannerGroups };
    });
  };

  // ---------- Add/remove contents ----------
  const addBannerContent = (groupIndex, bannerIndex) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      const group = { ...groups[groupIndex] };
      const banners = [...group.banners];
      const banner = { ...banners[bannerIndex] };

      banner.bannerContents = [...banner.bannerContents, createEmptyBannerContent()];

      banners[bannerIndex] = banner;
      group.banners = banners;
      groups[groupIndex] = group;

      return { ...p, bannerGroups: groups };
    });
  };

  const removeBannerContent = (groupIndex, bannerIndex, contentIndex) => {
    setPayload((p) => {
      const groups = [...p.bannerGroups];
      const group = { ...groups[groupIndex] };
      const banners = [...group.banners];
      const banner = { ...banners[bannerIndex] };

      const nextContents = banner.bannerContents.filter((_, i) => i !== contentIndex);
      if (!nextContents.length) return p;

      banner.bannerContents = nextContents;

      banners[bannerIndex] = banner;
      group.banners = banners;
      groups[groupIndex] = group;

      return { ...p, bannerGroups: groups };
    });
  };

  // ---------- Submit (EDIT) ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!payload) return;

    try {
      setSaving(true);
      setErrorMsg('');

      const formData = new FormData();

      const appendValue = (key, value) => {
        const safe = value === null || value === undefined ? '' : value;
        formData.append(key, safe);
      };

      // REQUIRED extra param for edit
      appendValue('id', payload.id);

      // Top level fields (same as create)
      appendValue('pageCode', payload.pageCode);
      appendValue('name', payload.name);
      appendValue('displayOrder', payload.displayOrder);
      appendValue('blockHeader', payload.blockHeader);
      appendValue('subHeader', payload.subHeader);
      appendValue('blockContent', payload.blockContent);
      appendValue('link', payload.link);
      appendValue('validFrom', toApiDateTime(payload.validFrom));
      appendValue('validTill', toApiDateTime(payload.validTill));
      appendValue('isPublic', payload.isPublic);
      appendValue('isVisible', payload.isVisible);

      // bannerGroups loop (no optional group keys)
      payload.bannerGroups.forEach((group, gi) => {
        group.banners.forEach((banner, bi) => {
          appendValue(`bannerGroups[${gi}][banners][${bi}][mainTitle]`, banner.mainTitle);
          appendValue(`bannerGroups[${gi}][banners][${bi}][subTitle]`, banner.subTitle);
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][shortDescription]`,
            banner.shortDescription
          );
          appendValue(`bannerGroups[${gi}][banners][${bi}][description]`, banner.description);
          appendValue(`bannerGroups[${gi}][banners][${bi}][groupOrder]`, banner.groupOrder);
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][validFrom]`,
            toApiDateTime(banner.validFrom)
          );
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][validTill]`,
            toApiDateTime(banner.validTill)
          );
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][isActionButtonEnable]`,
            banner.isActionButtonEnable
          );

          // actionButtonModal
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][actionButtonModal][text]`,
            banner.actionButtonModal.text
          );
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][actionButtonModal][hrefLink]`,
            banner.actionButtonModal.hrefLink
          );
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][actionButtonModal][isNewTab]`,
            banner.actionButtonModal.isNewTab
          );
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][actionButtonModal][validFrom]`,
            toApiDateTime(banner.actionButtonModal.validFrom)
          );
          appendValue(
            `bannerGroups[${gi}][banners][${bi}][actionButtonModal][validTill]`,
            toApiDateTime(banner.actionButtonModal.validTill)
          );

          // contents
          banner.bannerContents.forEach((c, ci) => {
            appendValue(`bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][alt]`, c.alt);
            appendValue(
              `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][isClickable]`,
              c.isClickable
            );
            appendValue(
              `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][clickLink]`,
              c.clickLink
            );
            appendValue(
              `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][displayOrder]`,
              c.displayOrder
            );
            appendValue(
              `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][validFrom]`,
              toApiDateTime(c.validFrom)
            );
            appendValue(
              `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][validTill]`,
              toApiDateTime(c.validTill)
            );

            // Files: ONLY append if user selected a new file
            if (c.imageFile) {
              formData.append(
                `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][imageFile]`,
                c.imageFile
              );
            }
            if (c.mobileImageFile) {
              formData.append(
                `bannerGroups[${gi}][banners][${bi}][bannerContents][${ci}][mobileImageFile]`,
                c.mobileImageFile
              );
            }
          });
        });
      });

      const res = await axiosInstance.post(
        `/internal-api/v1/client-web/cms/page/page-blocks/banner-group/${id}/edit-group`,
        formData
      );

      if (!res?.data?.status) {
        setErrorMsg(res?.data?.error?.message || 'Update failed');
        return;
      }

      alert('Banner group updated successfully!');
      
      // Navigate based on whether it's a custom page block or landing page block
      if (pageMeta) {
        // Custom page block - navigate back to CustomPageBlocks
        const navParams = new URLSearchParams();
        if (pageId) navParams.set('pageId', pageId);
        navParams.set('pageName', pageMeta);
        navigate(`/CustomPageBlocks?${navParams.toString()}`);
      } else {
        // Landing page block - navigate to LandingPage
        navigate('/LandingPage');
      }
    } catch (err) {
      console.error('EditBannerGroup submit error:', err);
      setErrorMsg(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          'Request failed'
      );
    } finally {
      setSaving(false);
    }
  };

  // ---------- UI states ----------
  if (loading) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-lg">
        <div className="text-gray-700">Loading...</div>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-lg">
        <div className="mb-3 text-red-600">{errorMsg || 'No data'}</div>
        <button
          type="button"
          onClick={loadForEdit}
          className="px-4 py-2 text-white bg-blue-600 rounded-md"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-800">Edit Banner Group #{id}</h2>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={loadForEdit}
            className="px-4 py-2 text-white bg-blue-600 rounded-md"
          >
            Refresh
          </button>

          <button
            type="button"
            onClick={() => {
              // Navigate based on whether it's a custom page block or landing page block
              if (pageMeta) {
                const navParams = new URLSearchParams();
                if (pageId) navParams.set('pageId', pageId);
                navParams.set('pageName', pageMeta);
                navigate(`/CustomPageBlocks?${navParams.toString()}`);
              } else {
                navigate('/LandingPage');
              }
            }}
            className="px-4 py-2 text-white bg-gray-700 rounded-md"
          >
            Back
          </button>
        </div>
      </div>

      {errorMsg && (
        <div className="p-3 mb-4 text-sm text-red-700 border border-red-200 rounded-md bg-red-50">
          {errorMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Top-level fields */}
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {/* <div>
            <label className="block text-sm font-medium text-gray-700">Page Code</label>
            <input
              type="text"
              value={payload.pageCode}
              onChange={(e) => setTopField('pageCode', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              placeholder="HOME"
            />
          </div> */}

          <div>
            <label className="block text-sm font-medium text-gray-700">Name</label>
            <input
              type="text"
              value={payload.name}
              onChange={(e) => setTopField('name', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              placeholder="HomeBanner"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Display Order</label>
            <input
              type="number"
              value={payload.displayOrder}
              onChange={(e) => setTopNumberField('displayOrder', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
              min={1}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Is Public</label>
            <select
              value={payload.isPublic}
              onChange={(e) => setTopNumberField('isPublic', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          </div>
        </div>

        <div>
            <label className="block text-sm font-medium text-gray-700">Is visible</label>
            <select
              value={payload.isVisible}
              onChange={(e) => setTopNumberField('isVisible', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              <option value={1}>Yes</option>
              <option value={0}>No</option>
            </select>
          </div>

        {/* <div className="grid grid-cols-1 gap-3 md:grid-cols-2">

        
          <div>
            <label className="block text-sm font-medium text-gray-700">Block Header</label>
            <input
              type="text"
              value={payload.blockHeader}
              onChange={(e) => setTopField('blockHeader', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Sub Header</label>
            <input
              type="text"
              value={payload.subHeader}
              onChange={(e) => setTopField('subHeader', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
        </div> */}

        {/* <div>
          <label className="block text-sm font-medium text-gray-700">Block Content</label>
          <input
            type="text"
            value={payload.blockContent}
            onChange={(e) => setTopField('blockContent', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
          />
        </div> */}

        <div>
          <label className="block text-sm font-medium text-gray-700">Link</label>
          <input
            type="text"
            value={payload.link}
            onChange={(e) => setTopField('link', e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md"
          />
        </div>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700">Valid From</label>
            <input
              type="datetime-local"
              value={payload.validFrom}
              onChange={(e) => setTopField('validFrom', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Valid Till</label>
            <input
              type="datetime-local"
              value={payload.validTill}
              onChange={(e) => setTopField('validTill', e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>

        {/* Render Banners by GROUP */}
        {payload.bannerGroups.map((group, gi) => {
          const banner = group.banners[0];
          const bi = 0;

          return (
            <div key={gi} className="p-4 border rounded-md">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-medium text-gray-800">Banner {gi + 1}</h3>

                {payload.bannerGroups.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeBannerGroup(gi)}
                    className="px-3 py-1 text-sm text-white bg-red-600 rounded-md"
                  >
                    Remove Banner
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Main Title</label>
                  <input
                    type="text"
                    value={banner.mainTitle}
                    onChange={(e) => setBannerField(gi, bi, 'mainTitle', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Sub Title</label>
                  <input
                    type="text"
                    value={banner.subTitle}
                    onChange={(e) => setBannerField(gi, bi, 'subTitle', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Short Description</label>
                  <input
                    type="text"
                    value={banner.shortDescription}
                    onChange={(e) => setBannerField(gi, bi, 'shortDescription', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Group Order</label>
                  <input
                    type="number"
                    value={banner.groupOrder}
                    onChange={(e) => setBannerField(gi, bi, 'groupOrder', Number(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    min={1}
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={banner.description}
                  onChange={(e) => setBannerField(gi, bi, 'description', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  rows={3}
                />
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700">Action Button Enable</label>
                <select
                  value={banner.isActionButtonEnable}
                  onChange={(e) =>
                    setBannerField(gi, bi, 'isActionButtonEnable', Number(e.target.value))
                  }
                  className="w-full p-2 border border-gray-300 rounded-md"
                >
                  <option value={0}>Disabled</option>
                  <option value={1}>Enabled</option>
                </select>
              </div>

              {banner.isActionButtonEnable === 1 && (
                <div className="p-3 mt-3 border rounded-md">
                  <h4 className="mb-2 text-sm font-semibold text-gray-700">Action Button Modal</h4>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Text</label>
                      <input
                        type="text"
                        value={banner.actionButtonModal.text}
                        onChange={(e) => setActionButtonField(gi, bi, 'text', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">Href Link</label>
                      <input
                        type="text"
                        value={banner.actionButtonModal.hrefLink}
                        onChange={(e) => setActionButtonField(gi, bi, 'hrefLink', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Open In New Tab</label>
                      <select
                        value={banner.actionButtonModal.isNewTab}
                        onChange={(e) =>
                          setActionButtonField(gi, bi, 'isNewTab', Number(e.target.value))
                        }
                        className="w-full p-2 border border-gray-300 rounded-md"
                      >
                        <option value={0}>No</option>
                        <option value={1}>Yes</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Valid From</label>
                      <input
                        type="datetime-local"
                        value={banner.actionButtonModal.validFrom}
                        onChange={(e) =>
                          setActionButtonField(gi, bi, 'validFrom', e.target.value)
                        }
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Valid Till</label>
                      <input
                        type="datetime-local"
                        value={banner.actionButtonModal.validTill}
                        onChange={(e) =>
                          setActionButtonField(gi, bi, 'validTill', e.target.value)
                        }
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Contents */}
              <div className="p-3 mt-4 border rounded-md">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-700">Banner Contents</h4>
                  <button
                    type="button"
                    onClick={() => addBannerContent(gi, bi)}
                    className="px-3 py-1 text-sm text-white bg-blue-600 rounded-md"
                  >
                    Add Banner Content
                  </button>
                </div>

                {banner.bannerContents.map((c, ci) => (
                  <div key={ci} className="p-3 mt-3 border rounded-md">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-medium text-gray-700">Content {ci + 1}</h5>
                      {banner.bannerContents.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeBannerContent(gi, bi, ci)}
                          className="px-2 py-1 text-xs text-white bg-red-600 rounded-md"
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Alt</label>
                        <input
                          type="text"
                          value={c.alt}
                          onChange={(e) => setContentField(gi, bi, ci, 'alt', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">Display Order</label>
                        <input
                          type="number"
                          value={c.displayOrder}
                          onChange={(e) =>
                            setContentField(gi, bi, ci, 'displayOrder', Number(e.target.value))
                          }
                          className="w-full p-2 border border-gray-300 rounded-md"
                          min={1}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Is Clickable</label>
                        <select
                          value={c.isClickable}
                          onChange={(e) =>
                            setContentField(gi, bi, ci, 'isClickable', Number(e.target.value))
                          }
                          className="w-full p-2 border border-gray-300 rounded-md"
                        >
                          <option value={0}>No</option>
                          <option value={1}>Yes</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Click Link</label>
                        <input
                          type="text"
                          value={c.clickLink}
                          onChange={(e) => setContentField(gi, bi, ci, 'clickLink', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Valid From</label>
                        <input
                          type="datetime-local"
                          value={c.validFrom}
                          onChange={(e) => setContentField(gi, bi, ci, 'validFrom', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Valid Till</label>
                        <input
                          type="datetime-local"
                          value={c.validTill}
                          onChange={(e) => setContentField(gi, bi, ci, 'validTill', e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>

                    {/* Existing image previews (from GET) */}
                    <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                      <ImageCard title="Current Web Image" url={c.webImgPath} />
                      <ImageCard title="Current Mobile Image" url={c.mobImgPath} />
                    </div>

                    {/* New file upload (optional) */}
                    <div className="grid grid-cols-1 gap-3 mt-3 md:grid-cols-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Replace Web Image (optional)
                        </label>
                        <input
                          type="file"
                          onChange={(e) =>
                            setContentField(
                              gi,
                              bi,
                              ci,
                              'imageFile',
                              e.target.files && e.target.files[0] ? e.target.files[0] : null
                            )
                          }
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Replace Mobile Image (optional)
                        </label>
                        <input
                          type="file"
                          onChange={(e) =>
                            setContentField(
                              gi,
                              bi,
                              ci,
                              'mobileImageFile',
                              e.target.files && e.target.files[0] ? e.target.files[0] : null
                            )
                          }
                          className="w-full p-2 border border-gray-300 rounded-md"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={addBannerGroup}
          className="px-4 py-2 text-white bg-indigo-600 rounded-md"
        >
          Add Another Banner
        </button>

        <button
          type="submit"
          disabled={saving}
          className={`px-6 py-2 text-white rounded-md ${
            saving ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600'
          }`}
        >
          {saving ? 'Saving...' : 'Update Banner Group'}
        </button>
      </form>
    </div>
  );
};

const ImageCard = ({ title, url }) => {
  return (
    <div className="p-3 border rounded-md">
      <div className="mb-2 text-xs font-semibold text-gray-700">{title}</div>
      {url ? (
        <>
          <img
            src={url}
            alt={title}
            className="object-cover w-full h-40 border rounded-md"
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
          <div className="mt-2 text-[10px] text-blue-700 break-all">{url}</div>
        </>
      ) : (
        <div className="text-sm text-gray-600">No image</div>
      )}
    </div>
  );
};

export default EditBannerGroup;
